import React, { FC, useEffect, useState } from "react";
import {
  ActionTypeEnum,
  BeneficiaryResponseModel,
  BeneficiaryTypeEnum,
  LookupKeyValueModel,
  RequestActionModel,
} from "../../models";
import { ButtonBox, LoadingBox, TextBox } from "..";
import { getLabelName, isArabicCurrentLanguage } from "../../utils";
import _ from "lodash";
import { getBeneficiaryByType } from "../../serviceBroker/beneficiaryApiServiceBroker";
import {
  beneficiaryCancelSearchBtnStyle, beneficiaryCloseSearchModelBtnStyle, beneficiaryInfoButtonStyle,
  beneficiarySearchBtnStyle, beneficiarySearchTextBoxStyle,
} from "../transactions/common/css/posCss";

export const BeneficiaryButtonList: FC<{
  type: BeneficiaryTypeEnum;
  onActionEvent: (o: RequestActionModel) => void;
}> = ({ type, onActionEvent = () => {} }) => {
  //#region variables
  const isArabic = isArabicCurrentLanguage();
  //#endregion
  //#region state
  const [filterText, setFilterText] = useState<string | null>(null);
  const [beneficiaryList, setBeneficiaryList] = useState<
    BeneficiaryResponseModel[]
  >([]);
  const [filteredBeneficiaryList, setFilteredBeneficiaryList] = useState<
    BeneficiaryResponseModel[]
  >([]);
  const [loading, setLoading] = useState(false);
  //const [rowPattern, setRowPattern] = useState<number[]>([5]);
  const [buttonWidth, setButtonWidth] = useState("190px");
  //#endregion
  //#region useEffect
  useEffect(() => {
    const updateLayout = () => {
      const width = window.innerWidth;
      if (width < 600) {
        setButtonWidth("140px");
      } else if (width >= 600 && width < 1000) {
        setButtonWidth("140px");
      } else if (width >= 1000 && width < 1200) {
        setButtonWidth("190px");
      } else {
        setButtonWidth("200px");
      }
    };
    // Call both synchronously
    updateLayout();
    window.addEventListener("resize", updateLayout);
    const handler = setTimeout(async () => {
      setLoading(true); // ✅ Show loading
      try {
        await handleSearchBeneficiary();
      } finally {
        setLoading(false); // ✅ Hide loading (always called)
      }
    }, filterText === null ? 0 : 550);
    return () => {
      window.removeEventListener("resize", updateLayout);
      clearTimeout(handler);
    };
  }, [type,filterText]);
  //#endregion
  //#region functions
  const getActionType = (): ActionTypeEnum => {
    switch (type) {
      case BeneficiaryTypeEnum.Customer:
        return ActionTypeEnum.SelectCustomerResponse;
      case BeneficiaryTypeEnum.Employee:
        return ActionTypeEnum.SelectEmployeeResponse;
    }
    return ActionTypeEnum.None;
  };
  const handleSearchBeneficiary = async (
    isClearSearch: boolean = false,
    searchText: string | null = null
  ) => {
    setLoading(true);
    let beneficiaries: BeneficiaryResponseModel[] =
      type === BeneficiaryTypeEnum.Employee ? beneficiaryList : [];
    let lowerQuery: string | null = !isClearSearch
      ? (searchText || filterText || "").toLowerCase()
      : "";
    lowerQuery = lowerQuery === "" ? null : lowerQuery;
    if (beneficiaries === null || beneficiaries.length === 0) {
      beneficiaries = handleSortAlphabetically(
        await getBeneficiaryByType(
          type,
          true,
          generateSearchRequest(lowerQuery)
        ),
        isArabic
      );
      setBeneficiaryList(beneficiaries);
      setFilteredBeneficiaryList(beneficiaries);
    } else {
      if (lowerQuery !== null) {
        const beneficiaries = beneficiaryList.filter((beneficiary) => {
          const searchInName = beneficiary.name
            .toLowerCase()
            .includes(lowerQuery || "");
          const searchInNameAr = beneficiary.nameAr
            .toLowerCase()
            .includes(lowerQuery || "");
          const searchInMobile =
            beneficiary.mobile &&
            /^\d+$/.test(beneficiary.mobile) &&
            beneficiary.mobile.includes(lowerQuery || "");
          return _.some([searchInName, searchInNameAr, searchInMobile]);
        });
        setFilteredBeneficiaryList(beneficiaries);
      } else {
        setFilteredBeneficiaryList(beneficiaryList);
      }
    }
    setLoading(false);
  };
  const handleSortAlphabetically = (
    list: BeneficiaryResponseModel[],
    isArabic: boolean
  ) => {
    return list.slice().sort((a, b) => {
      const propertyName = isArabic ? "nameAr" : "name";
      const nameA = a[propertyName].toUpperCase();
      const nameB = b[propertyName].toUpperCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });
  };
  const generateSearchRequest = (
    filterText: string | null = null
  ): LookupKeyValueModel[] => {
    let searchValues: LookupKeyValueModel[] = [];
    if (filterText !== null && filterText !== "") {
      switch (type) {
        case BeneficiaryTypeEnum.Customer:
          searchValues.push({ key: "serachParmeter.name", value: filterText });
          searchValues.push({
            key: "serachParmeter.mobile",
            value: filterText,
          });
      }
    }
    return searchValues;
  };
  //#endregion
  //#region html
  return (
    <>
      {loading && <LoadingBox />}
      <div
          className="d-flex flex-wrap justify-content-center align-items-center gap-2 mx-auto"
          style={{
            width: "100%",
            maxWidth: "1000px",
          }}
      >
        <TextBox
          labelName={getLabelName("")} //{t("lookup.nameAr")}
          inputName={"filterTextBox"}
          inputValue={filterText}
          onChange={(e: any) => {
            setFilterText(e.target.value);
          }}
          placeHolder={`Search for a ${type === BeneficiaryTypeEnum.Supplier ? "Supplier" : "Customer"}....`}
          style={beneficiarySearchTextBoxStyle}
                controlSize="w-sm-75 w-md-75 w-lg-25 w-xl-25 w-xxl-25"
        />
        <ButtonBox
          //iconType="magnify"
          type="submit"
          className="btn mx-2"
          variant=""
          onClick={async () => {
            await handleSearchBeneficiary();
          }}
          style={beneficiarySearchBtnStyle}
        >
          {getLabelName("Search")}
        </ButtonBox>
        <ButtonBox
         // iconType="close-circle"
          type="button"
          className="btn mx-2"
          variant=""
          onClick={async () => {
            //setFilteredBeneficiaryList(beneficiaryList);
            setFilterText("");
            await handleSearchBeneficiary(true);
          }}
          style={beneficiaryCancelSearchBtnStyle}
        >
          {getLabelName("Cancel")}
        </ButtonBox>
        <ButtonBox
          //iconType="close-circle"
          type="button"
          className="btn mx-2"
          variant=""
          onClick={async () => {
            onActionEvent({
              action: ActionTypeEnum.None,
            });
          }}
          style={beneficiaryCloseSearchModelBtnStyle}
        >
          {getLabelName("Close")}
        </ButtonBox>
      </div>
      <ul className="align-items-start align-content-start d-flex flex-wrap pos-list  p-2">
        {filteredBeneficiaryList !== null &&
          filteredBeneficiaryList.length !== 0 &&
          filteredBeneficiaryList.map((row, index) => (
            <li className="list-group-item p-1 w-auto border-0" key={index}>
              <ButtonBox
                key={`beneficiary_btn_${index}`}
                variant="light"
                className="shadow-sm border-0 fw-semibold"
                style={{...beneficiaryInfoButtonStyle ,width: buttonWidth}}
                id={`beneficiary_btn_${index}`}
                onClick={() => {
                  onActionEvent({
                    action: getActionType(),
                    request: row,
                  });
                }}
              >
                {isArabic ? row.nameAr : row.name}
                {row.mobile ? `-${row.mobile}` : ""}
                {row.address ? `-${row.address}` : ""}
              </ButtonBox>
            </li>
          ))}
      </ul>
    </>
  );
  //#endregion
};
